import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Edit2, Trash2, Upload, FileText, Download, X, Plus, Save, MessageSquare, CheckSquare, Send,
} from "lucide-react";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { StatusChip, StateChip, OverallScoreChip } from "./Dashboard";
import ScoreModule from "./modules/ScoreModule";
import TesmModule from "./modules/TesmModule";
import BnbdModule from "./modules/BnbdModule";
import PceiModule from "./modules/PceiModule";
import PcabModule from "./modules/PcabModule";
import TapiModule from "./modules/TapiModule";
import CostSummary from "./modules/CostSummaryModule";
import WinProbability from "./modules/WinProbability";
import TenderResultPanel from "@/components/TenderResultPanel";
import FrameworkLockGate from "@/components/FrameworkLockGate";
import { useAuth } from "@/context/AuthContext";
import PlanGate from "@/components/PlanGate";
import { canAccess } from "@/lib/plans";
import { Lock as LockIcon } from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "tesm", label: "TESM" },
  { key: "bnbd", label: "BNBD™" },
  { key: "pcei", label: "PCEI™" },
  { key: "pcab", label: "PCAB™" },
  { key: "tapi", label: "TAPI™" },
  { key: "documents", label: "Documents" },
  { key: "cost", label: "Cost Summary" },
  { key: "score", label: "Tender Result" },
];

const STAGES = ["identified", "assessing", "bidding", "submitted", "not_submitted", "awarded"];
const STAGE_LABELS = {
  identified:    "Identified",
  assessing:     "Assessing",
  bidding:       "Bidding",
  submitted:     "Submitted",
  not_submitted: "Not Submitted",
  awarded:       "Awarded",
};
const STATUSES = ["active", "submitted", "won", "lost"];

export default function TenderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const [tender, setTender] = useState(null);
  const [tab, setTab] = useState(initialTab && TABS.some((t) => t.key === initialTab) ? initialTab : "overview");
  const [schema, setSchema] = useState(null);
  const [scores, setScores] = useState(null);
  const [editing, setEditing] = useState(false);

  const loadAll = useCallback(async () => {
    const [{ data: t }, { data: sc }, { data: sch }] = await Promise.all([
      api.get(`/tenders/${id}`),
      api.get(`/tenders/${id}/scores`),
      api.get(`/modules/schema`),
    ]);
    setTender(t); setScores(sc); setSchema(sch);
  }, [id]);

  useEffect(() => { loadAll().catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message)); }, [loadAll]);

  const refreshScores = async () => {
    const { data } = await api.get(`/tenders/${id}/scores`);
    setScores(data);
  };

  const remove = async () => {
    if (!confirm("Delete this tender?")) return;
    await api.delete(`/tenders/${id}`);
    toast.success("Tender deleted");
    nav("/tenders");
  };

  if (!tender) return <div className="text-sm text-ink-muted uppercase tracking-wider">Loading…</div>;

  const stageIdx = STAGES.indexOf(tender.stage);

  const markSubmitted = async () => {
    if (!window.confirm("Mark this tender as Submitted? This cannot be undone in v1.")) return;
    try {
      const { data } = await api.post(`/tenders/${id}/mark-submitted`, { note: "" });
      setTender(data);
      toast.success("Tender marked as Submitted");
    } catch (ex) {
      const det = ex.response?.data?.detail;
      toast.error(typeof det === "string" ? det : (det?.message || ex.message));
    }
  };

  return (
    <div className="space-y-6" data-testid="tender-detail-page">
      <button onClick={() => nav(-1)} className="text-[12px] uppercase tracking-wider text-ink-muted hover:text-ink flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      {/* Header */}
      <div className="border-b border-ink-line pb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            {tender.sr_no != null && <span className="chip border-ink-line font-num">SR. {tender.sr_no}</span>}
            {tender.tender_no && <span className="chip border-ink-line font-num" data-testid="tender-no-chip">{tender.tender_no}</span>}
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">
              {tender.tender_type || tender.sector || "Tender"}
            </div>
            {tender.gem_type && <span className="chip border-brand/30 bg-brand-soft text-brand">{tender.gem_type}</span>}
            {tender.supply_service && <span className="chip border-ink-line">{tender.supply_service}</span>}
            <StatusChip status={tender.status} />
            <StateChip state={tender.state} />
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{tender.title}</h1>
          <div className="text-sm text-ink-muted mt-2">
            {tender.client} · {tender.location || "—"} ·{" "}
            <span className="font-num">{tender.contract_value || (tender.value_estimate ? `${tender.currency} ${Number(tender.value_estimate).toLocaleString()}` : "Value TBD")}</span>
            {" "}· Closing <span className="font-num">{tender.deadline || "—"}</span>
          </div>
        </div>
        <div className="flex gap-2 items-start flex-wrap">
          <OverallScoreChip tenderId={id} />
          {tender.state === "ACTIVE" && (
            <button onClick={markSubmitted} data-testid="mark-submitted-btn" className="flex items-center gap-1.5 px-3 py-2 text-[13px] bg-brand text-white rounded-sm hover:bg-brand-hover transition-colors">
              <Send className="w-3.5 h-3.5" /> Mark as Submitted
            </button>
          )}
          <button onClick={() => setEditing(true)} data-testid="tender-edit-btn" className="flex items-center gap-1.5 px-3 py-2 text-[13px] border border-ink-line rounded-sm hover:bg-secondary transition-colors">
            <Edit2 className="w-3.5 h-3.5" /> Edit
          </button>
          <button onClick={remove} data-testid="tender-delete-btn" className="flex items-center gap-1.5 px-3 py-2 text-[13px] border border-warn/30 text-warn rounded-sm hover:bg-warn-soft transition-colors">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>

      {/* Lifecycle tracker */}
      <div className="card-flat p-6" data-testid="lifecycle-tracker">
        <div className="stat-label mb-4">Lifecycle</div>
        <div className="flex items-center">
          {STAGES.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 grid place-items-center text-xs font-num font-bold rounded-sm border ${
                  i <= stageIdx ? "bg-brand border-brand text-white" : "bg-white border-ink-line text-ink-muted"
                }`}>{i + 1}</div>
                <div className={`text-[10px] uppercase tracking-wider mt-2 text-center ${i === stageIdx ? "text-brand font-medium" : "text-ink-muted"}`}>{STAGE_LABELS[s] || s}</div>
              </div>
              {i < STAGES.length - 1 && (
                <div className={`h-px flex-1 mx-2 mb-5 ${i < stageIdx ? "bg-brand" : "bg-ink-line"}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-0 border-b border-ink-line">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-4 py-2.5 text-[13px] font-medium -mb-px border-b-2 transition-colors ${
              tab === t.key ? "border-brand text-brand" : "border-transparent text-ink-muted hover:text-ink"
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              {t.label}
              {!canAccess(user, t.key) && <LockIcon className="w-3 h-3 opacity-60" data-testid={`tab-${t.key}-lock`} />}
            </span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {tab === "overview" && <Overview tender={tender} scores={scores} />}
        {tab === "tesm" && <PlanGate compact feature="tesm" title="TESM"><TesmModule tenderId={id} schema={schema?.tesm} scoreDoc={scores?.modules?.tesm} onSaved={refreshScores} /></PlanGate>}
        {tab === "bnbd" && <PlanGate compact feature="bnbd" title="BNBD™ Bid/No-Bid Diagnostic"><FrameworkLockGate framework="BNBD"><BnbdModule tenderId={id} /></FrameworkLockGate></PlanGate>}
        {tab === "pcei" && <PlanGate compact feature="pcei" title="PCEI™ Pre-Contract Exposure Index"><FrameworkLockGate framework="PCEI"><PceiModule tenderId={id} /></FrameworkLockGate></PlanGate>}
        {tab === "pcab" && <PlanGate compact feature="pcab" title="PCAB™ Pricing Benchmark"><FrameworkLockGate framework="PCAB"><PcabModule tenderId={id} /></FrameworkLockGate></PlanGate>}
        {tab === "tapi" && <PlanGate compact feature="tapi" title="TAPI™ Performance Indicator"><TapiModule tenderId={id} /></PlanGate>}
        {tab === "cost" && <CostSummary tenderId={id} />}
        {tab === "documents" && <Documents tenderId={id} />}
        {tab === "team" && <Collaboration tenderId={id} />}
        {tab === "score" && <BidScore scores={scores} tenderId={id} />}
      </div>

      {editing && <EditModal tender={tender} onClose={() => setEditing(false)} onSaved={(t) => { setTender(t); setEditing(false); toast.success("Tender updated"); }} />}
    </div>
  );
}

function Overview({ tender, scores }) {
  const a = scores?.aggregate || {};
  const [ov, setOv] = useState(null);

  useEffect(() => {
    if (!tender?.id) return;
    api.get(`/tenders/${tender.id}/overall-score`).then(({ data }) => setOv(data)).catch(() => setOv(null));
  }, [tender?.id, a.bnbd, a.pcab, a.pcei, a.tapi]);

  const overall = ov?.overall;
  const fwDetails = ov?.framework_details || {};
  const bidScore = ov?.bid_score;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-up" data-testid="overview-tab">
      <div className="lg:col-span-2 space-y-6">
        <div className="card-flat p-6">
          <div className="stat-label mb-3">Description</div>
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{tender.description || "No description provided."}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-ink-line">
            <Meta label="Stage" value={STAGE_LABELS[tender.stage] || tender.stage} />
            <Meta label="Status" value={tender.status} />
            <Meta label="Sector" value={tender.sector || "—"} />
            <Meta label="Created by" value={tender.created_by_name || "—"} />
          </div>
        </div>

        {/* Bid Score (TAPI-based, distinct from Overall) — dashboard_score_fixes.md §5A */}
        <div className="card-flat p-6" data-testid="bid-score-card">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="stat-label">Bid Score</div>
              <div className="text-[11px] text-ink-muted mt-0.5 uppercase tracking-wider">Result-oriented · from TAPI</div>
            </div>
            {bidScore && <TierPill detail={bidScore} />}
          </div>
          <div className="mt-3 font-num font-bold text-4xl" data-testid="bid-score-value">
            {bidScore ? `${bidScore.score}` : <span className="text-ink-muted text-2xl">Not Started</span>}
            {bidScore && <span className="text-ink-muted text-lg font-normal"> / 100</span>}
          </div>
          {!bidScore && (
            <div className="text-xs text-ink-muted mt-2">Complete TAPI to see the Bid Score for this tender.</div>
          )}
        </div>
      </div>

      {/* Overall Score column with per-framework breakdown */}
      <div className="card-flat p-6" data-testid="overall-score-card">
        <div className="stat-label mb-1">Overall Score</div>
        <div className="text-[11px] text-ink-muted uppercase tracking-wider mb-4">Pre-bid health · BNBD + PCEI + PCAB</div>
        <div className="text-center py-2">
          <div className="font-num font-bold text-5xl tracking-tight" data-testid="overall-score-value">
            {overall ? `${overall.score}` : <span className="text-ink-muted text-3xl">—</span>}
            {overall && <span className="text-ink-muted text-2xl font-normal"> / 100</span>}
          </div>
          {overall && <div className="mt-3 inline-block"><TierPill detail={overall} large /></div>}
          {!overall && ov?.missing?.length > 0 && (
            <div className="text-xs text-ink-muted mt-3">Complete {ov.missing.join(", ")} to compute</div>
          )}
        </div>
        <div className="border-t border-ink-line mt-5 pt-4 grid grid-cols-3 gap-2">
          <FwBox label="BNBD" detail={fwDetails.bnbd} testid="fw-bnbd" />
          <FwBox label="PCEI" detail={fwDetails.pcei} testid="fw-pcei" />
          <FwBox label="PCAB" detail={fwDetails.pcab} testid="fw-pcab" />
        </div>
      </div>
    </div>
  );
}

function FwBox({ label, detail, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-display font-bold">{label}</div>
      {detail ? (
        <>
          <div className="font-num font-bold text-xl mt-1">{detail.score}<span className="text-ink-muted text-[11px] font-normal">/100</span></div>
          <div className="mt-1"><TierPill detail={detail} small /></div>
        </>
      ) : (
        <div className="font-num text-base text-ink-muted mt-1">Not Started</div>
      )}
    </div>
  );
}

function TierPill({ detail, large = false, small = false }) {
  const cls = (() => {
    const c = detail.color;
    if (c === "dark_green") return "bg-good-soft text-good border-good/50";
    if (c === "green") return "bg-good-soft/70 text-good border-good/30";
    if (c === "amber") return "bg-warn-soft text-warn border-warn/40";
    if (c === "orange") return "bg-warn-soft/90 text-warn-strong border-warn/60";
    if (c === "red") return "bg-red-50 text-red-700 border-red-300";
    return "bg-brand-soft text-brand border-brand/40";
  })();
  const size = large ? "text-xs px-3 py-1" : small ? "text-[9px] px-1.5 py-0.5" : "text-[10px] px-2 py-0.5";
  return <span className={`chip border ${cls} ${size} font-display font-bold tracking-tight`}>{detail.band}</span>;
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div className="text-sm font-medium mt-1 capitalize">{value}</div>
    </div>
  );
}

function Documents({ tenderId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [tag, setTag] = useState("rfq");

  const load = async () => {
    const { data } = await api.get(`/tenders/${tenderId}/documents`);
    setDocs(data);
  };
  useEffect(() => { load(); }, [tenderId]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tag", tag);
    fd.append("version", "v1");
    setUploading(true);
    try {
      await api.post(`/tenders/${tenderId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const download = async (doc) => {
    const resp = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url; a.download = doc.original_filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const remove = async (doc) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/documents/${doc.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-4 animate-fade-up" data-testid="documents-tab">
      <div className="card-flat p-5 flex flex-wrap items-end gap-3">
        <div>
          <label className="stat-label block mb-1.5">Tag</label>
          <select value={tag} onChange={(e) => setTag(e.target.value)} className="px-3 py-2 border border-ink-line bg-white rounded-sm text-sm">
            {["rfq", "technical", "commercial", "compliance", "drawing", "other"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <label className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-sm text-sm font-medium cursor-pointer transition-colors" data-testid="upload-document-btn">
          <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload file"}
          <input type="file" className="hidden" onChange={onUpload} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" disabled={uploading} />
        </label>
        <div className="text-xs text-ink-muted">PDF, Word, Excel and images supported.</div>
      </div>

      <div className="card-flat">
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b border-ink-line">
            <tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted">
              <th className="px-5 py-3 font-medium">File</th>
              <th className="px-5 py-3 font-medium">Tag</th>
              <th className="px-5 py-3 font-medium">Version</th>
              <th className="px-5 py-3 font-medium">Uploaded by</th>
              <th className="px-5 py-3 font-medium">Date</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && (<tr><td colSpan={6} className="px-5 py-10 text-center text-ink-muted">No documents uploaded</td></tr>)}
            {docs.map((d) => (
              <tr key={d.id} className="border-b border-ink-line last:border-b-0 hover:bg-secondary/60 transition-colors">
                <td className="px-5 py-3 font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4 text-brand shrink-0" /> <span className="truncate max-w-xs">{d.original_filename}</span>
                </td>
                <td className="px-5 py-3"><span className="chip">{d.tag || "—"}</span></td>
                <td className="px-5 py-3 font-num text-ink-muted">{d.version}</td>
                <td className="px-5 py-3 text-ink-muted">{d.uploaded_by_name}</td>
                <td className="px-5 py-3 text-ink-muted font-num text-xs">{new Date(d.uploaded_at).toLocaleDateString()}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => download(d)} data-testid={`download-doc-${d.id}`} className="p-1.5 hover:bg-secondary rounded-sm text-ink-muted hover:text-brand transition-colors"><Download className="w-4 h-4" /></button>
                  <button onClick={() => remove(d)} data-testid={`delete-doc-${d.id}`} className="p-1.5 hover:bg-warn-soft rounded-sm text-ink-muted hover:text-warn transition-colors ml-1"><X className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Collaboration({ tenderId }) {
  const [comments, setComments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [text, setText] = useState("");
  const [newTask, setNewTask] = useState({ title: "", assignee: "", due_date: "", status: "open" });

  const load = async () => {
    const [{ data: c }, { data: t }] = await Promise.all([
      api.get(`/tenders/${tenderId}/comments`),
      api.get(`/tenders/${tenderId}/tasks`),
    ]);
    setComments(c); setTasks(t);
  };
  useEffect(() => { load(); }, [tenderId]);

  const addComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await api.post(`/tenders/${tenderId}/comments`, { text });
    setText(""); load();
  };

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTask.title.trim()) return;
    await api.post(`/tenders/${tenderId}/tasks`, newTask);
    setNewTask({ title: "", assignee: "", due_date: "", status: "open" });
    load();
  };

  const toggleTask = async (t) => {
    const next = t.status === "done" ? "open" : "done";
    await api.patch(`/tasks/${t.id}`, { status: next });
    load();
  };

  const deleteTask = async (t) => {
    await api.delete(`/tasks/${t.id}`);
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-up" data-testid="collaboration-tab">
      <div className="card-flat">
        <div className="px-5 py-4 border-b border-ink-line flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-brand" />
          <div className="font-display font-semibold">Comments</div>
        </div>
        <div className="p-5 space-y-4 max-h-[480px] overflow-y-auto nice-scroll">
          {comments.length === 0 && <div className="text-sm text-ink-muted">No comments yet.</div>}
          {comments.map((c) => (
            <div key={c.id} className="flex gap-3">
              <div className="w-8 h-8 bg-brand-soft text-brand grid place-items-center font-semibold text-[11px] rounded-sm shrink-0">
                {(c.author_name || "U").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="text-xs text-ink-muted">
                  <span className="font-medium text-ink">{c.author_name}</span> · {new Date(c.created_at).toLocaleString()}
                </div>
                <div className="text-sm mt-1 whitespace-pre-wrap">{c.text}</div>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={addComment} className="p-4 border-t border-ink-line flex gap-2" data-testid="comment-form">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment…" data-testid="comment-input" className="flex-1 px-3 py-2 border border-ink-line rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" />
          <button type="submit" className="bg-brand hover:bg-brand-hover text-white px-4 py-2 text-sm font-medium rounded-sm" data-testid="comment-submit">Post</button>
        </form>
      </div>

      <div className="card-flat">
        <div className="px-5 py-4 border-b border-ink-line flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-brand" />
          <div className="font-display font-semibold">Tasks</div>
        </div>
        <div className="divide-y divide-ink-line max-h-[380px] overflow-y-auto nice-scroll">
          {tasks.length === 0 && <div className="px-5 py-8 text-sm text-ink-muted">No tasks yet.</div>}
          {tasks.map((t) => (
            <div key={t.id} className="px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <input type="checkbox" checked={t.status === "done"} onChange={() => toggleTask(t)} className="accent-brand" data-testid={`task-checkbox-${t.id}`} />
                <div className="min-w-0">
                  <div className={`text-sm font-medium ${t.status === "done" ? "line-through text-ink-muted" : ""}`}>{t.title}</div>
                  <div className="text-xs text-ink-muted">
                    {t.assignee || "Unassigned"} {t.due_date ? `· Due ${t.due_date}` : ""}
                  </div>
                </div>
              </div>
              <button onClick={() => deleteTask(t)} className="p-1 text-ink-muted hover:text-warn rounded-sm"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <form onSubmit={addTask} className="p-4 border-t border-ink-line space-y-2" data-testid="task-form">
          <input value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} placeholder="Task title" data-testid="task-title-input" className="w-full px-3 py-2 border border-ink-line rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" />
          <div className="grid grid-cols-2 gap-2">
            <input value={newTask.assignee} onChange={(e) => setNewTask({ ...newTask, assignee: e.target.value })} placeholder="Assignee" className="px-3 py-2 border border-ink-line rounded-sm text-sm bg-white" />
            <input type="date" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} className="px-3 py-2 border border-ink-line rounded-sm text-sm bg-white" />
          </div>
          <button type="submit" data-testid="task-submit" className="w-full bg-brand hover:bg-brand-hover text-white px-3 py-2 text-sm font-medium rounded-sm flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Add task</button>
        </form>
      </div>
    </div>
  );
}

function BidScore({ scores, tenderId }) {
  const a = scores?.aggregate || {};
  const data = [
    { k: "TRCA", v: a.trca || 0, color: "#002FA7" },
    { k: "TESM", v: a.tesm || 0, color: "#002FA7" },
    { k: "BNBD", v: a.bnbd || 0, color: "#002FA7" },
    { k: "PCEI", v: a.pcei || 0, color: "#FF5A00" },
    { k: "PCAB", v: a.pcab || 0, color: "#002FA7" },
    { k: "TAPI", v: a.tapi || 0, color: "#009E60" },
  ];
  const reco = a.bnbd_recommendation || "PENDING";
  const recoColor = reco === "BID" ? "bg-good text-white" : reco === "CONDITIONAL" ? "bg-warn text-white" : reco === "NO-BID" ? "bg-ink text-white" : "bg-secondary text-ink-muted";

  return (
    <div className="space-y-6 animate-fade-up" data-testid="bid-score-tab">
      {/* Tender Result + Bid Score upload (position-based, per spec) */}
      <TenderResultPanel tenderId={tenderId} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-flat p-6 lg:col-span-2">
          <div className="stat-label mb-3">Framework Aggregate</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 15, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="k" tick={{ fontSize: 12, fill: "#111" }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#666" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#F3F4F6" }} contentStyle={{ fontSize: 12, borderRadius: 2, border: "1px solid #E5E7EB" }} />
                <Bar dataKey="v" radius={[2, 2, 0, 0]}>
                  {data.map((d, i) => (<Cell key={i} fill={d.color} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-6 flex flex-col">
          <div className="stat-label mb-2">BNBD Recommendation</div>
          <div className={`inline-block px-4 py-2 rounded-sm font-display font-bold tracking-tight ${recoColor}`} data-testid="bnbd-recommendation">{reco}</div>
        </div>
      </div>
    </div>
  );
}

function EditModal({ tender, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [f, setF] = useState({
    title: tender.title || "",
    client: tender.client || "",
    location: tender.location || "",
    gem_type: tender.gem_type || "GeM",
    deadline: tender.deadline || "",
    tender_type: tender.tender_type || "Other",
    tender_type_other: tender.tender_type_other || "",
    supply_service: tender.supply_service || "Supply",
    contract_value: tender.contract_value || "",
    tender_no: tender.tender_no || "",
    description: tender.description || "",
    status: tender.status,
    stage: tender.stage,
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Strip `stage` for non-admins so the field is never sent (backend also enforces).
      const payload = isAdmin ? f : (({ stage, ...rest }) => rest)(f);
      const { data } = await api.patch(`/tenders/${tender.id}`, payload);
      onSaved(data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  const inputCls = "w-full px-3 py-2 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand";

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-modal">
      <div className="bg-white max-w-2xl w-full border border-ink-line rounded-sm max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-line sticky top-0 bg-white">
          <div className="font-display font-bold tracking-tight">Edit Tender · SR. {tender.sr_no ?? "—"}</div>
          <button onClick={onClose} className="p-1 text-ink-muted hover:text-ink"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4">
          <div><label className="stat-label block mb-1.5">Client</label><input value={f.client} onChange={(e) => setF({ ...f, client: e.target.value })} className={inputCls} data-testid="edit-client-input" /></div>
          <div><label className="stat-label block mb-1.5">Location</label><input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={inputCls} placeholder="City, State" /></div>
          <div className="col-span-2"><label className="stat-label block mb-1.5">Tender Title</label><textarea rows={2} maxLength={500} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} data-testid="edit-title-input" /></div>
          <div><label className="stat-label block mb-1.5">GeM / Non-GeM</label>
            <select value={f.gem_type} onChange={(e) => setF({ ...f, gem_type: e.target.value })} className={inputCls}>
              {["GeM", "GeMNCB", "NCB", "Non-GeM", "NewNCB", "CORNCB", "NewCORNCB"].map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div><label className="stat-label block mb-1.5">Closing Date</label><input type="date" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} className={inputCls} /></div>
          <div><label className="stat-label block mb-1.5">Tender No.</label><input value={f.tender_no} onChange={(e) => setF({ ...f, tender_no: e.target.value })} maxLength={100} className={inputCls} placeholder="e.g., GT-76463" data-testid="edit-tender-no-input" /></div>
          <div><label className="stat-label block mb-1.5">Type</label>
            <input value={f.tender_type} onChange={(e) => setF({ ...f, tender_type: e.target.value })} maxLength={100} className={inputCls} placeholder="Free text · max 100 chars" data-testid="edit-type-input" />
          </div>
          <div><label className="stat-label block mb-1.5">Supply / Service</label>
            <select value={f.supply_service} onChange={(e) => setF({ ...f, supply_service: e.target.value })} className={inputCls}>
              {["Supply", "Service", "Supply & Service"].map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="col-span-2"><label className="stat-label block mb-1.5">Contract Value</label><input value={f.contract_value} onChange={(e) => setF({ ...f, contract_value: e.target.value })} className={inputCls} placeholder='e.g., "INR 12 Cr" or "Refer Document"' /></div>
          <div><label className="stat-label block mb-1.5">Stage {isAdmin ? "" : "(admin only)"}</label>
            <select
              value={f.stage}
              onChange={(e) => setF({ ...f, stage: e.target.value })}
              disabled={!isAdmin}
              className={`${inputCls} ${!isAdmin ? "opacity-60 cursor-not-allowed bg-secondary" : ""}`}
              data-testid="edit-stage-select"
            >
              {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s] || s}</option>)}
            </select>
          </div>
          <div><label className="stat-label block mb-1.5">Status</label>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inputCls} data-testid="edit-status-select">
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-span-2"><label className="stat-label block mb-1.5">Description (optional)</label><textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} /></div>
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-ink-line rounded-sm hover:bg-secondary transition-colors">Cancel</button>
            <button type="submit" disabled={loading} data-testid="edit-save-btn" className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-1.5 transition-colors">
              <Save className="w-4 h-4" /> {loading ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
